import { useRelatedItem } from "@hooks/useRelatedItem";
import { matchRelatedItem } from "@utils/related-items.util";
import { RelationObjectType } from "@enums/relations.enum";
import { DocumentType } from "@enums/common.enum";
import { RelatedEntity } from "@app-types/related-items.type";
import { IDocumentTagResponse, ITagResponse } from "@interfaces/responses.interface";
import { SuggestionWeb } from "@components/TaggingComponent/SuggestionItems/SuggestionWeb";
import { Tag } from "@components/Tags/Tag";
import { Tooltip } from "@ui/Tooltip";
import { Button } from "@ui/Button";
import { Pill } from "@ui/Pill";

const TOOLTIP_CAPTION_PREVIEW_LENGTH = 132;

interface ICitationComponentProps {
  children: string;
  sources: Record<number, RelatedEntity>;
  order: Record<string, number>;
}

const getReferenceComponent = (reference: RelatedEntity, iconOnly?: boolean) => {
  return (
    <div className="flex flex-col min-w-0 max-w-[200px]">
      {matchRelatedItem(reference, {
        onMeeting: (meeting) => (
          <Tag
            data={{
              id: meeting.id,
              object: {
                id: meeting.id,
                title: meeting.title,
                object_type: meeting.object_type || "meetings.meeting",
                type: meeting.type,
                start_time: meeting.start_time,
              },
              object_type: meeting.object_type || "meetings.meeting",
              pending: false,
            }}
            isDisabled={true}
            iconOnly={iconOnly}
          />
        ),
        onDeal: (deal) => (
          <Tag
            data={{
              id: deal.id,
              object: {
                id: deal.id,
                title: deal.title,
                stage_color: deal.stage?.color,
                object_type: RelationObjectType.DEAL,
                company: null,
              },
              object_type: RelationObjectType.DEAL,
              pending: false,
            }}
            isDisabled={true}
            iconOnly={iconOnly}
          />
        ),
        onDocument: (document) => (
          <Tag
            data={
              {
                id: document.id,
                object: {
                  id: document.id,
                  name: document.file_name || document.name,
                  object_type: RelationObjectType.DOCUMENT,
                  type: DocumentType.DOCUMENT,
                },
                object_type: RelationObjectType.DOCUMENT,
                pending: false,
              } as unknown as IDocumentTagResponse
            }
            isDisabled={true}
            iconOnly={iconOnly}
          />
        ),
        onFolder: (folder) => (
          <Tag
            data={{
              id: folder.id,
              object: {
                id: folder.id,
                name: folder.name,
                object_type: RelationObjectType.FOLDER,
              },
              object_type: RelationObjectType.FOLDER,
              pending: false,
            }}
            isDisabled={true}
            iconOnly={iconOnly}
          />
        ),
        onThemes: (theme) => (
          <Tag
            data={{
              id: theme.id,
              object: {
                id: theme.id,
                object_type: RelationObjectType.THEME,
                title: theme.title,
                level: theme.level,
              },
              object_type: RelationObjectType.THEME,
              pending: false,
            }}
            isDisabled={true}
            iconOnly={iconOnly}
          />
        ),
        onThread: (email) => (
          <Tag
            data={{
              id: email.thread_id || email.id,
              object: {
                id: email.thread_id || email.id,
                subject: email.subject,
                sent_date: email.sent_date,
                object_type: RelationObjectType.EMAIL_THREAD,
              },
              object_type: RelationObjectType.EMAIL_THREAD,
              pending: false,
            }}
            isDisabled={true}
            iconOnly={iconOnly}
          />
        ),
        onWebpage: (webpage) =>
          iconOnly ? (
            <img src={webpage.favicon} alt="favicon" className="h-5 w-5 object-cover" />
          ) : (
            <SuggestionWeb variant="small" item={webpage} />
          ),
        onNote: (note) => (
          <Tag
            data={{
              id: note.id,
              object: {
                id: note.id,
                title: note.title,
                object_type: RelationObjectType.NOTE,
                access: note.access,
                state: "",
              },
              object_type: RelationObjectType.NOTE,
              pending: false,
            }}
            isDisabled={true}
            iconOnly={iconOnly}
          />
        ),
        onCompanies: (company) => (
          <Tag
            data={{
              id: company.id,
              object: {
                id: company.id,
                title: company.title,
                object_type: RelationObjectType.COMPANY,
                logo: company.logo_url ?? company.logo,
                deal: null,
                website: company.website ?? null,
              },
              object_type: RelationObjectType.COMPANY,
              pending: false,
            }}
            isDisabled={true}
            iconOnly={iconOnly}
          />
        ),
        onPeople: (person) => (
          <Tag
            data={{
              id: person.id,
              object: {
                id: person.id,
                first_name: person.first_name,
                last_name: person.last_name,
                email: person.email,
                object_type: RelationObjectType.PERSON,
                logo: person.primary_company?.company.logo_url ?? null,
              },
              object_type: RelationObjectType.PERSON,
              pending: false,
            }}
            isDisabled={true}
            iconOnly={iconOnly}
          />
        ),
      })}

      <Tag data={reference as unknown as ITagResponse} />

      {"caption" in reference && reference.caption && (
        <div className="line-clamp-2 text-sm text-content-secondary">
          {reference.caption.slice(0, TOOLTIP_CAPTION_PREVIEW_LENGTH)}
          {reference.caption.length > TOOLTIP_CAPTION_PREVIEW_LENGTH ? "..." : ""}
        </div>
      )}
    </div>
  );
};

export function CitationComponent({ children, sources, order }: ICitationComponentProps) {
  const index = Number(Array.isArray(children) ? children.join("") : children);
  const reference = sources[index];
  const iconOnly = true;

  const { handleClick } = useRelatedItem(reference);

  if (!reference) return null;

  return (
    <Tooltip
      as="span"
      tooltipContentClassName="p-0 min-w-0"
      tooltipContent={getReferenceComponent(reference)}
      offset={8}
    >
      <button onClick={handleClick} className="h-4 bg-transparent p-0 mx-1">
        {getReferenceComponent(reference, iconOnly)}
      </button>
    </Tooltip>
  );
}
